import ExcelJS from 'exceljs/dist/exceljs.min.js';

const COLOR_PRIMARIO = 'FF20453D';
const COLOR_ENCABEZADO_TABLA = 'FFF3F4F6';
const COLOR_FILA_ALTERNA = 'FFFAFAFA';
const COLOR_BORDE = 'FFDDE1E5';
const COLOR_TEXTO_TITULO = 'FFFFFFFF';
const COLOR_TEXTO_SUBTITULO = 'FF6B7280';
const COLOR_TEXTO_ENCABEZADO = 'FF374151';

const FORMATO_MONTO = '"RD$" #,##0.00';
const FORMATO_ENTERO = '#,##0';
const FORMATO_FECHA = 'dd/mm/yyyy';

function formatFecha(fecha) {
  if (!fecha) return '-';
  const [y, m, d] = fecha.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' });
}

function fechaComoDate(fecha) {
  if (!fecha) return null;
  const [y, m, d] = fecha.split('-').map(Number);
  return new Date(y, m - 1, d);
}

async function descargarWorkbook(workbook, nombreArchivo) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function crearWorkbook() {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'OmniERP';
  workbook.created = new Date();
  return workbook;
}

/**
 * Escribe el bloque de título + período en las primeras filas de la hoja
 * (mismo contenido que el encabezado del PDF). Devuelve la fila donde debe
 * empezar la tabla de datos.
 */
function escribirEncabezadoHoja(hoja, titulo, filtros, numColumnas) {
  hoja.mergeCells(1, 1, 1, numColumnas);
  const celdaTitulo = hoja.getCell(1, 1);
  celdaTitulo.value = titulo;
  celdaTitulo.font = { bold: true, size: 14, color: { argb: COLOR_TEXTO_TITULO } };
  celdaTitulo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_PRIMARIO } };
  celdaTitulo.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  hoja.getRow(1).height = 26;

  hoja.mergeCells(2, 1, 2, numColumnas);
  const celdaPeriodo = hoja.getCell(2, 1);
  const notaComprobante = filtros.solo_con_comprobante ? ' · Solo facturas con comprobante fiscal (NCF)' : '';
  const generado = new Date().toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' });
  celdaPeriodo.value = `${formatFecha(filtros.fecha_desde)} - ${formatFecha(filtros.fecha_hasta)}${notaComprobante}   ·   Generado: ${generado}`;
  celdaPeriodo.font = { italic: true, size: 10, color: { argb: COLOR_TEXTO_SUBTITULO } };
  celdaPeriodo.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  hoja.getRow(2).height = 18;

  return 4;
}

/**
 * Escribe una tabla (encabezado + filas + fila de totales opcional) a partir
 * de la fila indicada, aplica anchos de columna, autofiltro y congela el
 * encabezado. Devuelve la siguiente fila libre en la hoja.
 */
function escribirTabla(hoja, filaInicio, columnas, filas, { filaTotales = null } = {}) {
  const filaEncabezado = hoja.getRow(filaInicio);
  columnas.forEach((col, i) => {
    const celda = filaEncabezado.getCell(i + 1);
    celda.value = col.titulo;
    celda.font = { bold: true, color: { argb: COLOR_TEXTO_ENCABEZADO } };
    celda.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_ENCABEZADO_TABLA } };
    celda.alignment = { horizontal: col.align === 'right' ? 'right' : 'left', vertical: 'middle' };
    celda.border = { bottom: { style: 'thin', color: { argb: COLOR_BORDE } } };
  });
  filaEncabezado.height = 20;

  filas.forEach((fila, idx) => {
    const filaExcel = hoja.getRow(filaInicio + 1 + idx);
    columnas.forEach((col, i) => {
      const celda = filaExcel.getCell(i + 1);
      celda.value = fila[i];
      celda.alignment = { horizontal: col.align === 'right' ? 'right' : 'left' };
      if (col.formatoNumero) celda.numFmt = col.formatoNumero;
      if (idx % 2 === 1) {
        celda.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_FILA_ALTERNA } };
      }
    });
  });

  let filaSiguiente = filaInicio + 1 + filas.length;

  if (filaTotales) {
    const filaExcelTotales = hoja.getRow(filaSiguiente);
    columnas.forEach((col, i) => {
      const celda = filaExcelTotales.getCell(i + 1);
      celda.value = filaTotales[i];
      celda.font = { bold: true };
      celda.alignment = { horizontal: col.align === 'right' ? 'right' : 'left' };
      if (col.formatoNumero) celda.numFmt = col.formatoNumero;
      celda.border = { top: { style: 'thin', color: { argb: COLOR_PRIMARIO } } };
    });
    filaSiguiente += 1;
  }

  columnas.forEach((col, i) => {
    hoja.getColumn(i + 1).width = col.ancho || 18;
  });

  hoja.autoFilter = {
    from: { row: filaInicio, column: 1 },
    to: { row: filaInicio, column: columnas.length }
  };
  hoja.views = [{ state: 'frozen', ySplit: filaInicio }];

  return filaSiguiente;
}

/**
 * Genera y descarga el Excel del reporte de ventas (resumen) a partir de la
 * respuesta de GET /api/reporte/ventas-resumen. Una hoja por sección, mismos
 * datos que generarPDFReporteVentas.
 */
export default async function generarExcelReporteVentas(datos, filtros) {
  if (!datos) return;

  const workbook = crearWorkbook();

  // RESUMEN EJECUTIVO
  const hojaResumen = workbook.addWorksheet('Resumen Ejecutivo');
  let filaInicio = escribirEncabezadoHoja(hojaResumen, 'Reporte de Ventas', filtros, 2);
  const rg = datos.resumen_general;
  escribirTabla(hojaResumen, filaInicio, [
    { titulo: 'Indicador', align: 'left', ancho: 40 },
    { titulo: 'Valor', align: 'right', ancho: 22, formatoNumero: null }
  ], [
    ['Total de Ventas (Neto)', `RD$ ${rg.total_ventas_neto.toFixed(2)}`],
    ['Cantidad de Facturas', rg.cantidad_facturas],
    ['Ticket Promedio', `RD$ ${rg.ticket_promedio.toFixed(2)}`],
    ['Dinero Recibido', `RD$ ${datos.dinero_recibido.total_cobrado.toFixed(2)}`],
    ['Dinero por Cobrar', `RD$ ${datos.dinero_por_cobrar.total_pendiente.toFixed(2)}`],
    [`Ventas al Contado (${datos.ventas_credito_contado.contado.porcentaje}%)`, `RD$ ${datos.ventas_credito_contado.contado.monto_total.toFixed(2)}`],
    [`Ventas a Crédito (${datos.ventas_credito_contado.credito.porcentaje}%)`, `RD$ ${datos.ventas_credito_contado.credito.monto_total.toFixed(2)}`],
    [`Ventas Gratuitas (${datos.ventas_credito_contado.gratuito.porcentaje}%)`, `RD$ ${datos.ventas_credito_contado.gratuito.monto_total.toFixed(2)}`]
  ]);
  // Deja la columna "Valor" como texto (ya trae el prefijo RD$) para que el
  // resumen se lea igual que en el PDF sin depender del formato numérico.

  // POR TIPO DE COMPROBANTE
  const hojaTipo = workbook.addWorksheet('Por Tipo Comprobante');
  filaInicio = escribirEncabezadoHoja(hojaTipo, 'Ventas por Tipo de Comprobante', filtros, 6);
  const columnasTipo = [
    { titulo: 'Tipo de Comprobante', align: 'left', ancho: 34 },
    { titulo: 'Facturas', align: 'right', ancho: 12, formatoNumero: FORMATO_ENTERO },
    { titulo: 'Subtotal', align: 'right', ancho: 16, formatoNumero: FORMATO_MONTO },
    { titulo: 'ITBIS', align: 'right', ancho: 16, formatoNumero: FORMATO_MONTO },
    { titulo: 'Monto Total', align: 'right', ancho: 16, formatoNumero: FORMATO_MONTO },
    { titulo: '%', align: 'right', ancho: 10 }
  ];
  const totalesTipo = datos.por_tipo_comprobante.reduce((acc, t) => ({
    cantidad_facturas: acc.cantidad_facturas + t.cantidad_facturas,
    monto_bruto: acc.monto_bruto + t.monto_bruto,
    monto_impuesto: acc.monto_impuesto + t.monto_impuesto,
    monto_total: acc.monto_total + t.monto_total
  }), { cantidad_facturas: 0, monto_bruto: 0, monto_impuesto: 0, monto_total: 0 });
  escribirTabla(hojaTipo, filaInicio, columnasTipo, datos.por_tipo_comprobante.map(t => [
    t.descripcion_completa, t.cantidad_facturas, t.monto_bruto, t.monto_impuesto, t.monto_total, `${t.porcentaje_del_total}%`
  ]), {
    filaTotales: ['TOTALES', totalesTipo.cantidad_facturas, totalesTipo.monto_bruto, totalesTipo.monto_impuesto, totalesTipo.monto_total, '']
  });

  // DINERO RECIBIDO
  if (datos.dinero_recibido.por_metodo_pago.length > 0) {
    const hojaMetodos = workbook.addWorksheet('Dinero Recibido');
    filaInicio = escribirEncabezadoHoja(hojaMetodos, 'Dinero Recibido por Método de Pago', filtros, 4);
    escribirTabla(hojaMetodos, filaInicio, [
      { titulo: 'Método de Pago', align: 'left', ancho: 30 },
      { titulo: 'Transacciones', align: 'right', ancho: 16, formatoNumero: FORMATO_ENTERO },
      { titulo: 'Monto Total', align: 'right', ancho: 18, formatoNumero: FORMATO_MONTO },
      { titulo: '%', align: 'right', ancho: 10 }
    ], datos.dinero_recibido.por_metodo_pago.map(m => [
      m.metodo_pago, m.cantidad_transacciones, m.monto_total, `${m.porcentaje}%`
    ]), {
      filaTotales: ['TOTAL', datos.dinero_recibido.cantidad_pagos, datos.dinero_recibido.total_cobrado, '']
    });
  }

  // CUENTAS POR COBRAR
  const hojaCobrar = workbook.addWorksheet('Cuentas por Cobrar');
  filaInicio = escribirEncabezadoHoja(hojaCobrar, 'Cuentas por Cobrar', filtros, 3);
  const dpc = datos.dinero_por_cobrar;
  escribirTabla(hojaCobrar, filaInicio, [
    { titulo: 'Estado', align: 'left', ancho: 26 },
    { titulo: 'Facturas', align: 'right', ancho: 14, formatoNumero: FORMATO_ENTERO },
    { titulo: 'Monto', align: 'right', ancho: 18, formatoNumero: FORMATO_MONTO }
  ], [
    ['Vencidas', dpc.vencidas.cantidad, dpc.vencidas.total],
    ['Por Vencer (7 días)', dpc.por_vencer_7_dias.cantidad, dpc.por_vencer_7_dias.total],
    ['Vigentes', dpc.vigentes.cantidad, dpc.vigentes.total],
    ['Total Pendiente', dpc.cantidad_facturas_pendientes, dpc.total_pendiente]
  ]);

  // TENDENCIA
  if (datos.tendencia && datos.tendencia.length > 0) {
    const hojaTendencia = workbook.addWorksheet('Tendencia');
    filaInicio = escribirEncabezadoHoja(hojaTendencia, 'Tendencia de Ventas', filtros, 3);
    escribirTabla(hojaTendencia, filaInicio, [
      { titulo: 'Período', align: 'left', ancho: 18, formatoNumero: FORMATO_FECHA },
      { titulo: 'Facturas', align: 'right', ancho: 14, formatoNumero: FORMATO_ENTERO },
      { titulo: 'Monto Total', align: 'right', ancho: 18, formatoNumero: FORMATO_MONTO }
    ], datos.tendencia.map(t => [fechaComoDate(t.periodo), t.cantidad_facturas, t.monto_total]));
  }

  await descargarWorkbook(workbook, `reporte-ventas_${filtros.fecha_desde}_${filtros.fecha_hasta}.xlsx`);
}

/**
 * Genera y descarga el Excel del detalle de facturas con comprobante fiscal
 * a partir de la respuesta de GET /api/reporte/ventas-detallado. Misma
 * lógica de columnas opcionales que generarPDFReporteVentasDetallado (solo
 * se muestran si tienen algún monto distinto de cero en el conjunto filtrado).
 */
export async function generarExcelReporteVentasDetallado(datos, filtros) {
  if (!datos) return;

  const TODAS_LAS_COLUMNAS = [
    { clave: 'fecha', titulo: 'Fecha', align: 'left', ancho: 13, formatoNumero: FORMATO_FECHA, valor: v => fechaComoDate(v) },
    { clave: 'rnc', titulo: 'RNC', align: 'left', ancho: 14, valor: v => v || '' },
    { clave: 'ncf', titulo: 'NCF', align: 'left', ancho: 16, valor: v => v || '' },
    { clave: 'cliente', titulo: 'Cliente', align: 'left', ancho: 32, valor: v => v || '' },
    { clave: 'exento', titulo: 'Exento', align: 'right', ancho: 14, formatoNumero: FORMATO_MONTO, opcional: true },
    { clave: 'gravado', titulo: 'Gravado', align: 'right', ancho: 14, formatoNumero: FORMATO_MONTO },
    { clave: 'itbis', titulo: 'Itbis', align: 'right', ancho: 14, formatoNumero: FORMATO_MONTO },
    { clave: 'total', titulo: 'Total', align: 'right', ancho: 14, formatoNumero: FORMATO_MONTO },
    { clave: 'efectivo', titulo: 'Efectivo', align: 'right', ancho: 14, formatoNumero: FORMATO_MONTO, opcional: true },
    { clave: 'tarjeta', titulo: 'Tarjeta', align: 'right', ancho: 14, formatoNumero: FORMATO_MONTO, opcional: true },
    { clave: 'cheque', titulo: 'Cheque', align: 'right', ancho: 14, formatoNumero: FORMATO_MONTO, opcional: true },
    { clave: 'transferencia', titulo: 'Transferencia', align: 'right', ancho: 16, formatoNumero: FORMATO_MONTO, opcional: true }
  ];

  const columnasVisibles = TODAS_LAS_COLUMNAS.filter(col => {
    if (!col.opcional) return true;
    return parseFloat(datos.totales[col.clave] || 0) !== 0;
  });

  const workbook = crearWorkbook();
  const hoja = workbook.addWorksheet('Detalle Facturas');

  let filaInicio = escribirEncabezadoHoja(hoja, 'Detalle de Facturas con Comprobante Fiscal', filtros, columnasVisibles.length);

  if (datos.truncado) {
    hoja.mergeCells(3, 1, 3, columnasVisibles.length);
    const celdaAviso = hoja.getCell(3, 1);
    celdaAviso.value = `Aviso: se muestran las primeras ${datos.facturas.length.toLocaleString('es-DO')} de ${datos.total_facturas.toLocaleString('es-DO')} facturas encontradas.`;
    celdaAviso.font = { bold: true, color: { argb: 'FFC87800' }, size: 10 };
    filaInicio = 5;
  }

  const filas = datos.facturas.map(f => columnasVisibles.map(col => col.valor ? col.valor(f[col.clave]) : f[col.clave]));

  const filaTotales = columnasVisibles.map(col => {
    if (col.clave === 'fecha') return 'TOTALES';
    if (['rnc', 'ncf', 'cliente'].includes(col.clave)) return '';
    return datos.totales[col.clave];
  });

  escribirTabla(hoja, filaInicio, columnasVisibles.map(({ clave, ...resto }) => resto), filas, { filaTotales });

  await descargarWorkbook(workbook, `reporte-ventas-detallado_${filtros.fecha_desde}_${filtros.fecha_hasta}.xlsx`);
}
